import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { motion } from "framer-motion";
import { Plus, LogOut, CreditCard, Trash2, Edit, MoreVertical } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { storage } from "../lib/storage";

const Dashboard = () => {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const localCards = storage.getCards();
    setCards(localCards);
    setIsLoading(false);
  }, []);

  const handleDeleteCard = (cardId) => {
    storage.deleteCard(cardId);
    setCards(cards.filter(c => c.card_id !== cardId));
    toast.success("Card deleted");
  };

  // Get template info
  const getTemplateColor = (templateId) => {
    const colors = {
      "minimal-white": "#000000",
      "minimal-black": "#ffffff",
      "corporate-blue": "#1e40af",
      "corporate-gray": "#374151",
      "creative-gradient": "#7c3aed",
      "creative-bold": "#ea580c",
      "tech-dark": "#22c55e",
      "tech-neon": "#06b6d4",
      "elegant-gold": "#ca8a04",
      "elegant-silver": "#94a3b8",
      "nature-green": "#166534",
      "nature-earth": "#78350f",
      "modern-red": "#dc2626",
      "modern-purple": "#7c3aed",
      "classic-navy": "#1e3a5f",
      "classic-maroon": "#7f1d1d",
      "startup-orange": "#f97316",
      "startup-teal": "#14b8a6",
      "luxury-black": "#d4af37",
      "luxury-cream": "#1c1917",
      "playful-pink": "#ec4899",
      "playful-yellow": "#facc15",
      "finance-dark": "#10b981",
      "finance-light": "#059669",
    };
    return colors[templateId] || "#000000";
  };

  const getTemplateBg = (templateId) => {
    const darkTemplates = ["minimal-black", "tech-dark", "tech-neon", "elegant-gold", "elegant-silver", "luxury-black", "startup-orange", "startup-teal", "finance-dark"];
    return darkTemplates.includes(templateId) ? "#0a0a0a" : "#ffffff";
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-heading text-xl font-bold">
            CardStudio
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/templates">
              <Button
                className="rounded-full bg-primary text-primary-foreground h-10 px-6"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Card
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold mb-2">My Cards</h1>
            <p className="text-muted-foreground">
              {cards.length === 0
                ? "Create your first business card"
                : `${cards.length} card${cards.length !== 1 ? "s" : ""} created`}
            </p>
          </div>
          <Link to="/templates">
            <Button
              className="rounded-full bg-primary text-primary-foreground h-12 px-6"
              data-testid="create-new-card-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create New Card
            </Button>
          </Link>
        </div>

        {isLoading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-secondary animate-pulse rounded-2xl h-64" />
            ))}
          </div>
        ) : cards.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-24"
          >
            <div className="w-20 h-20 bg-secondary rounded-2xl flex items-center justify-center mx-auto mb-6">
              <CreditCard className="w-10 h-10 text-muted-foreground" />
            </div>
            <h2 className="text-xl font-semibold mb-2">No cards yet</h2>
            <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
              Start creating professional business cards in minutes with our easy-to-use editor.
            </p>
            <Link to="/templates">
              <Button className="rounded-full bg-primary text-primary-foreground" data-testid="empty-create-card-btn">
                <Plus className="w-5 h-5 mr-2" />
                Create Your First Card
              </Button>
            </Link>
          </motion.div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cards.map((card, index) => (
              <motion.div
                key={card.card_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="group"
              >
                <div className="modern-card-elevated border border-border/50 overflow-hidden">
                  {/* Card Preview */}
                  <div
                    className="aspect-[3.5/2] p-6 relative"
                    style={{ backgroundColor: getTemplateBg(card.template_id) }}
                  >
                    <div
                      className="w-8 h-8 rounded-lg mb-4"
                      style={{ backgroundColor: getTemplateColor(card.template_id) }}
                    />
                    <div style={{ color: getTemplateBg(card.template_id) === "#0a0a0a" ? "#ffffff" : "#0a0a0a" }}>
                      <p className="font-heading text-lg font-bold truncate">
                        {card.card_data?.full_name || "Your Name"}
                      </p>
                      <p className="text-sm opacity-70 truncate">
                        {card.card_data?.job_title || "Job Title"}
                      </p>
                    </div>

                    {/* Actions overlay */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <Link to={`/editor/${card.card_id}`}>
                        <Button
                          size="sm"
                          className="rounded-full bg-white text-black hover:bg-white/90"
                          data-testid={`edit-card-${card.card_id}`}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                      </Link>
                    </div>
                  </div>

                  {/* Card Info */}
                  <div className="p-4 flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">
                        {card.card_data?.company_name || "Untitled Card"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(card.updated_at).toLocaleDateString()}
                      </p>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="rounded-full shrink-0" data-testid={`card-menu-${card.card_id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link to={`/editor/${card.card_id}`} className="flex items-center gap-2 cursor-pointer">
                            <Edit className="w-4 h-4" />
                            Edit
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive cursor-pointer"
                          onClick={() => handleDeleteCard(card.card_id)}
                          data-testid={`delete-card-${card.card_id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
